import { Fragment } from "react";
import { Navigate, Outlet, Route, useParams, useLocation } from "react-router";
import { ProductDetails } from "./ProductDetails/ProductDetails";
import IntermediateProductList from "./ProductList/IntermediateProductList";
import { UpdateQuestionnaire } from "./ProductForm/UpdateProduct";
import { CreateIntermediateProduct } from "./ProductForm/CreateIntermediateProduct";
import { CREATE, MODIFY, PRODUCT, useFoodpilotContext, VIEW, VIEW_SSQ } from "src/context/FoodpilotContext";
import { Forbiddenpage } from "src/components/PageState/ForbiddenPage";
import { Slugs } from "src/context/ProtectedContext";
import { ProductScores } from "./ProductDetails/ProductScores";
import { Questionnaire } from "./ProductDetails/Questionnaire/Questionnaire";
import { ProductInformations } from "./ProductDetails/ProductInformations";
import { SectionResolver } from "./SectionResolver";
import { ProductQuestionnaire } from "./ProductDetails/ProductQuestionnaire";
import { Summary } from "./ProductDetails/Summary/Summary";
import { Ingredients } from "./ProductDetails/Entities/Ingredients/Ingredients";
import { Packagings } from "./ProductDetails/Entities/Packagings/Packagings";
import { ScoreDetail } from "src/components/ScoreDetail/ScoreDetail";
import { DimensionTreeSwitch } from "src/components/ScoreDetail/Dimensions/DimensionTreeSwitch";
import { ProductMetadataContextProvider } from "src/context/Product/Metadata/provider";
import { ProductOneScoreDetailProvider } from "./ProductDetails/Scores/ProductScoreContextProvider";
import { useProductMetadataContext } from "src/context/Product/Metadata/useProductMetadata";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { QuestionnaireUpdate } from "./ProductDetails/Questionnaire/QuestionnaireUpdate";
import { Loading } from "src/components/utils/Loading";
import { ProductValueChain } from "./ProductDetails/ProductValueChain";
import { EntityScoresProvider } from "src/context/Scores/provider";
import { ProductScoresRoute } from "./productRouter";
import { ProductScoresProtection } from "./ProductScoresProtection";
import { RouteErrorPage } from "src/components/PageState/RouteErrorPage";

// Component to redirect while preserving search parameters
const RedirectWithParams = ({ to }: { to: string }) => {
    const location = useLocation();
    return <Navigate to={`${to}${location.search}`} replace />;
};

export const useIntermediateProductRouter = () => {
    return (
        <Fragment>
            <Route path="intermediate-products" errorElement={<RouteErrorPage />}>
                <Route element={<IntermediateProductActionProtection slug={VIEW} />}>
                    <Route index element={<IntermediateProductList />} />
                </Route>

                <Route path="new/:startingPageId?" element={<IntermediateProductActionProtection slug={CREATE} />}>
                    <Route index element={<CreateIntermediateProduct />} />
                </Route>

                <Route path=":productId" element={<ExistingIntermediateProduct />}>
                    <Route path="edit/:startingPageId?" element={<IntermediateProductActionProtection slug={MODIFY} />}>
                        <Route index element={<UpdateQuestionnaire />} />
                    </Route>

                    <Route element={<ProductScoresRoute />}>
                        <Route path="details" element={<IntermediateProductActionProtection slug={VIEW} />}>
                            <Route index element={<RedirectWithParams to="scores" />} />

                            <Route element={<SectionResolver />}>
                                <Route element={<ProductDetails />}>
                                    <Route
                                        path="scores"
                                        element={
                                            <ProductScoresProtection>
                                                <Outlet />
                                            </ProductScoresProtection>
                                        }
                                    >
                                        <Route index element={<ProductScores />} />
                                        <Route path=":scoreId" element={<IntermediateProductOneScoreDetail />}>
                                            <Route index element={<ScoreDetail selectedTab={null} />} />
                                            <Route
                                                path="distribution"
                                                element={<ScoreDetail selectedTab={"distribution"} />}
                                            >
                                                <Route index element={<DimensionTreeSwitch mode={"distribution"} />} />
                                            </Route>
                                            <Route
                                                path="comparison"
                                                element={<ScoreDetail selectedTab={"comparison"} />}
                                            >
                                                <Route index element={<DimensionTreeSwitch mode={"comparison"} />} />
                                            </Route>
                                            <Route path="evolution" element={<ScoreDetail selectedTab={"evolution"} />}>
                                                <Route index element={<DimensionTreeSwitch mode={"evolution"} />} />
                                            </Route>
                                        </Route>
                                    </Route>

                                    <Route path="informations" element={<ProductInformations />} />

                                    <Route path="value-chain" element={<ProductValueChain />} />

                                    <Route element={<IntermediateProductActionProtection slug={VIEW_SSQ} />}>
                                        <Route path="questionnaire" element={<ProductQuestionnaire />}>
                                            <Route index element={<RedirectWithParams to="product" />} />
                                            <Route path="summary" element={<Summary />} />
                                            <Route path="product" element={<Questionnaire />}>
                                                <Route path="company/:productCompanyId">
                                                    <Route index element={<Questionnaire />} />
                                                </Route>
                                            </Route>
                                            <Route path="ingredients" element={<Ingredients />} />
                                            <Route path="packagings" element={<Packagings />} />
                                        </Route>
                                    </Route>
                                </Route>
                            </Route>
                            <Route element={<IntermediateProductActionProtection slug={VIEW_SSQ} />}>
                                <Route
                                    path="questionnaire/company/:productCompanyId/edit/:headingId"
                                    element={
                                        <ProductScoresProtection>
                                            <QuestionnaireUpdate />
                                        </ProductScoresProtection>
                                    }
                                />
                            </Route>
                        </Route>
                    </Route>
                </Route>
            </Route>
        </Fragment>
    );
};

type IntermediateProductActionProtectionProps = {
    slug: Slugs;
};
const IntermediateProductActionProtection = (props: IntermediateProductActionProtectionProps) => {
    const { slug } = props;
    const { canUser } = useFoodpilotContext();

    // Intermediate products use the same permissions as regular products
    if (canUser(slug, PRODUCT) === false) {
        return <Forbiddenpage />;
    }

    return <Outlet />;
};

const ExistingIntermediateProduct = () => {
    const { productId } = useParams();

    return (
        <EntityScoresProvider entity="product" allowMissingScores>
            <ProductMetadataContextProvider productId={productId}>
                <Outlet />
            </ProductMetadataContextProvider>
        </EntityScoresProvider>
    );
};

const IntermediateProductOneScoreDetail = () => {
    const { company } = useCompanyMetadata();
    const { product, ssqVersion, isSsqVersionReady } = useProductMetadataContext();

    const { scoreId } = useParams();

    if (scoreId === undefined || isSsqVersionReady === false) {
        return <Loading />;
    }

    return (
        <ProductOneScoreDetailProvider
            companyId={company.id}
            productId={product.id}
            scoreId={Number(scoreId)}
            ssqVersion={ssqVersion}
            isIntermediateProduct={true}
        >
            <Outlet />
        </ProductOneScoreDetailProvider>
    );
};
